import { Text, type UseModalState } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { useGetAgreements } from "@src/appV2/Agreements/api/useGetAgreements";
import { AgreementKind } from "@src/appV2/Agreements/types";
import { FullScreenDialog } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { PdfViewerProvider } from "@src/appV2/PdfViewer/context";
import { StickyScrollPageHeader } from "@src/appV2/redesign/components/StickyScrollPageHeader";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useRef } from "react";

import { useHasBookedInterview } from "../api/useHasBookedInterview";
import { JobSearchAgreementForm } from "../SignJobSearchAgreement/components/JobSearchAgreementForm";

interface JobSearchAgreementModalProps {
  modalState: UseModalState;
  onComplete: () => void;
  placementId?: string;
}

export function JobSearchAgreementModal(props: JobSearchAgreementModalProps) {
  const { modalState, onComplete, placementId } = props;

  const worker = useDefinedWorker();
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: agreementsData, status: agreementsStatus } = useGetAgreements({
    workerId: worker.userId,
    kind: [AgreementKind.JOB_SEARCH],
  });

  const { hasBookedInterview } = useHasBookedInterview({
    workerId: worker.userId,
  });

  useLogEffect(
    APP_V2_USER_EVENTS.JOB_SEARCH_AGREEMENT_MODAL_VIEWED,
    { workerId: worker.userId, placementId, viewedAtStage: "BOOKING" },
    { enabled: modalState.modalIsOpen }
  );

  const agreementToSign = agreementsData?.data[0];

  function getAgreementsStatus(): "loading" | "error" | "success" {
    if (agreementsStatus === "loading") {
      return "loading";
    }
    if (agreementsStatus === "error" || !agreementToSign) {
      return "error";
    }
    return "success";
  }

  return (
    <FullScreenDialog skipTopSafeAreaPadding modalState={modalState} variant="tertiary">
      <StickyScrollPageHeader
        title=""
        headerVariant="tertiary"
        scrollContainerRef={scrollRef}
        navigateBack={() => {
          modalState.closeModal();
        }}
      />

      <Stack spacing={4} sx={{ px: 5, pt: 0 }}>
        <Text semibold variant="h5">
          {hasBookedInterview
            ? "To continue applying for jobs on Clipboard, please accept our updated Job Search User Agreement."
            : "To apply for jobs on Clipboard, please accept our Job Search User Agreement."}
        </Text>

        <Text color={(theme) => theme.palette.text.secondary}>
          This standard agreement covers how we connect you with employers, privacy protections, and
          dispute resolution.
        </Text>
      </Stack>

      <PdfViewerProvider
        fileName="clipboard_health_job_search_agreement.pdf"
        pdfUrl={agreementToSign?.attributes.downloadUrl}
      >
        <JobSearchAgreementForm
          scrollContainerRef={scrollRef}
          signedAtStage="BOOKING"
          worker={worker}
          agreementToSign={agreementToSign}
          agreementsStatus={getAgreementsStatus()}
          placementId={placementId}
          onAcceptAgreement={async () => {
            logEvent(APP_V2_USER_EVENTS.JOB_SEARCH_AGREEMENT_SIGNED, {
              workerId: worker.userId,
              placementId,
              agreementVersion: agreementToSign?.attributes.version,
              signedAtStage: "BOOKING",
            });
            onComplete();
          }}
        />
      </PdfViewerProvider>
    </FullScreenDialog>
  );
}
